/**
 * Tain Web Push client helpers.
 *
 *   getSubscription()       — current PushSubscription for this device (or null)
 *   ensureSubscribed()      — subscribe (asking OS permission if needed) and POST to backend
 *   unsubscribe()           — unsubscribe from browser + tell backend to forget this endpoint
 *   pushSupportedInBrowser  — quick capability check
 */
import api from "./api";

export const pushSupportedInBrowser =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function urlB64ToUint8Array(base64) {
  // The VAPID public key comes as URL-safe base64. The Push API wants a Uint8Array.
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  if (!pushSupportedInBrowser) return null;
  // If a SW registration already exists we reuse it, otherwise wait for it.
  const reg = await navigator.serviceWorker.getRegistration();
  return reg || (await navigator.serviceWorker.ready);
}

export async function getSubscription() {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Ask for permission (if not already granted), subscribe with the backend's
 * VAPID public key, and POST the subscription to /api/push/subscribe.
 *
 * Returns { ok, permission, subscription } — never throws.
 */
export async function ensureSubscribed() {
  if (!pushSupportedInBrowser) {
    return { ok: false, permission: "unsupported", reason: "unsupported_browser" };
  }
  // Ask backend if push is even enabled
  let publicKey;
  try {
    const r = await api.get("/push/public-key");
    if (!r.data?.enabled || !r.data?.public_key) {
      return { ok: false, permission: Notification.permission, reason: "server_disabled" };
    }
    publicKey = r.data.public_key;
  } catch {
    return { ok: false, permission: Notification.permission, reason: "server_unreachable" };
  }

  // Permission
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return { ok: false, permission, reason: "permission_denied" };
  }

  const reg = await getRegistration();
  if (!reg) return { ok: false, permission, reason: "no_sw" };

  // Reuse existing sub if it already matches this VAPID key; otherwise re-subscribe.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      // Ensure key still matches — if backend rotates the key we need a fresh sub.
      const existingKey = sub.options?.applicationServerKey;
      if (!existingKey) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch {
      /* ignore */
    }
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(publicKey),
      });
    } catch (e) {
      return { ok: false, permission, reason: "subscribe_failed", error: String(e) };
    }
  }

  const raw = sub.toJSON();
  try {
    await api.post("/push/subscribe", {
      endpoint: raw.endpoint,
      keys: { p256dh: raw.keys.p256dh, auth: raw.keys.auth },
      user_agent: navigator.userAgent,
    });
  } catch (e) {
    return { ok: false, permission, reason: "backend_save_failed", error: String(e) };
  }
  return { ok: true, permission, subscription: raw };
}

export async function unsubscribe() {
  const sub = await getSubscription();
  if (!sub) return { ok: true, reason: "no_subscription" };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch { /* ignore */ }
  try {
    await api.post("/push/unsubscribe", { endpoint });
  } catch { /* ignore */ }
  return { ok: true };
}
